import React, { useMemo, useState } from "react";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Box, Chip, Collapse, CircularProgress,
  Typography, Select, MenuItem, Button, Switch, FormControlLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, Checkbox, Tooltip, TablePagination,
  Stack
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import GetApp from "@mui/icons-material/GetApp";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";

const n = (v) => (v === "" || v == null ? null : Number(v));
const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");

/* ---------- Dialog de Subcategoria (add/edit) ---------- */
const SubDialog = ({ open, onClose, onSave, initial = {} }) => {
  const { t } = useTranslation("settings");
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    id: initial?.id ?? undefined,
    nome: initial?.nome ?? "",
    ordem: initial?.ordem ?? "",
    status: initial?.status == null ? 1 : Number(initial.status),
  });

  React.useEffect(() => {
    setForm({
      id: initial?.id ?? undefined,
      nome: initial?.nome ?? "",
      ordem: initial?.ordem ?? "",
      status: initial?.status == null ? 1 : Number(initial.status),
    });
  }, [initial?.id]);

  const canSave = form.nome.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? t("categoryTable.editSub") : t("categoryTable.newSub")}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 2 }}>
          <TextField
            label={t("shared.nameRequired")}
            value={form.nome}
            onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
          />
          <TextField
            label={t("shared.order")}
            value={form.ordem ?? ""}
            inputMode="numeric"
            onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={Number(form.status) === 1}
                onChange={(e) =>
                  setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))
                }
              />
            }
            label={Number(form.status) === 1 ? t("shared.activeFem") : t("shared.inactiveFem")}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("shared.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() =>
            onSave({
              id: form.id,
              nome: form.nome.trim(),
              ordem: n(form.ordem),
              status: Number(form.status) === 1 ? 1 : 0,
            })
          }
          disabled={!canSave}
        >
          {t("shared.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------- Tabela principal ---------- */
const CategoryTable = ({
  categories = [],
  rowCount = 0,
  page = 0,                 // zero-based
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEditCategory,
  onDeleteCategory,

  // Subcategorias
  onUpsertSubcategory,      // ({ parentId, sub })
  onDeleteSubcategory,      // ({ parentId, subId })

  // Toggles
  onToggleCategoryStatus,   // (cat)
  onToggleSubStatus,        // (parentId, sub)

  // Ações em massa
  onBulkActivate,           // (ids: number[])
  onBulkDeactivate,         // (ids: number[])
  onBulkDelete,             // (ids: number[])
}) => {
  const { t } = useTranslation("settings");
  const [localSearch, setLocalSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [selected, setSelected] = useState(new Set());

  // diálogo subcategoria
  const [subDialog, setSubDialog] = useState({ open: false, parentId: null, sub: null });

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const ordered = useMemo(
    () => categories.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [categories]
  );

  // seleção
  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allOnPageSelected =
    ordered.length > 0 && ordered.every((c) => selected.has(c.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const onPageIds = ordered.map((c) => c.id);
      const next = new Set(prev);
      if (allOnPageSelected) {
        onPageIds.forEach((id) => next.delete(id));
      } else {
        onPageIds.forEach((id) => next.add(id));
      }
      return next;
    });

  const selectedArr = Array.from(selected);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const exportColumns = useMemo(
    () => [
      { field: "ordem", headerName: t("shared.order") },
      { field: "nome", headerName: t("shared.name") },
      { field: "status", headerName: t("shared.status") },
    ],
    [t]
  );
  const selectedData = useMemo(
    () =>
      ordered
        .filter((c) => selected.has(c.id))
        .map((c) => ({
          ordem: String(c.ordem ?? ""),
          nome: c.nome ?? "—",
          status: Number(c.status) === 1 ? t("shared.active") : t("shared.inactive"),
        })),
    [ordered, selected, t]
  );
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text(t("categoryTable.exportTitle"), 20, 10);
    const headers = exportColumns.map((col) => col.headerName);
    const body = selectedData.map((row) => exportColumns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body });
    doc.save("categorias_produtos.pdf");
  };
  const csvData = useMemo(
    () =>
      selectedData.map((row) => {
        const o = {};
        exportColumns.forEach((c) => (o[c.headerName] = row[c.field]));
        return o;
      }),
    [selectedData, exportColumns]
  );

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder={t("shared.searchPlaceholder")}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Ações em massa padronizadas */}
      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("shared.selectedCount", { count: selectedArr.length })}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={exportPDF} sx={{ textTransform: "uppercase" }}>
            {t("shared.exportPdf")}
          </Button>
          <Button variant="contained" color="primary" startIcon={<GetApp />} sx={{ textTransform: "uppercase" }}>
            <CSVLink data={csvData} filename="categorias_produtos.csv" style={{ textDecoration: "none", color: "inherit" }}>
              {t("shared.exportCsv")}
            </CSVLink>
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            {`${t("shared.deleteSelected")} (${selectedArr.length})`}
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>{t("shared.deleteSelectedTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("categoryTable.bulkDeleteMsg")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>{t("shared.cancel")}</Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setConfirmBulkDelete(false);
            }}
          >
            {t("shared.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell width={48} />
              <TableCell>{t("shared.order")}</TableCell>
              <TableCell>{t("shared.name")}</TableCell>
              <TableCell>{t("shared.status")}</TableCell>
              <TableCell>{t("shared.subcategories")}</TableCell>
              <TableCell align="right">{t("shared.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">{t("shared.loading")}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("categoryTable.noCategories")}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((cat) => {
                const isOpen = expanded.has(cat.id);
                const subs = (cat.children || [])
                  .slice()
                  .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
                const checked = isRowSelected(cat.id);

                return (
                  <React.Fragment key={cat.id}>
                    <TableRow hover selected={checked}>
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelect(cat.id)}
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton size="small" onClick={() => toggleExpand(cat.id)}>
                          {isOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell>{cat.ordem ?? ""}</TableCell>
                      <TableCell>{cat.nome}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Switch
                            size="small"
                            checked={!!cat.status}
                            onChange={() => onToggleCategoryStatus?.(cat)}
                          />
                          <Chip
                            size="small"
                            label={cat.status ? t("shared.activeFem") : t("shared.inactiveFem")}
                            color={cat.status ? "success" : "default"}
                            variant="outlined"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={t("shared.itemsCount", { count: (cat.children || []).length })} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t("categoryTable.addSub")}>
                          <IconButton
                            size="small"
                            onClick={() => setSubDialog({ open: true, parentId: cat.id, sub: null })}
                          >
                            <PlaylistAddRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("shared.edit")}>
                          <IconButton size="small" onClick={() => onEditCategory?.(cat)}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("shared.delete")}>
                          <IconButton size="small" onClick={() => onDeleteCategory?.(cat.id)}>
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Subtabela */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
                            {subs.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {t("shared.noSubcategories")}
                              </Typography>
                            ) : (
                              <Table size="small" sx={{ background: "transparent" }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell width={48} />
                                    <TableCell>{t("shared.order")}</TableCell>
                                    <TableCell>{t("shared.name")}</TableCell>
                                    <TableCell>{t("shared.status")}</TableCell>
                                    <TableCell align="right">{t("shared.actions")}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {subs.map((s) => (
                                    <TableRow key={s.id}>
                                      <TableCell />
                                      <TableCell>{s.ordem ?? ""}</TableCell>
                                      <TableCell>{s.nome}</TableCell>
                                      <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                          <Switch
                                            size="small"
                                            checked={!!s.status}
                                            onChange={() => onToggleSubStatus?.(cat.id, s)}
                                          />
                                          <Chip
                                            size="small"
                                            label={s.status ? t("shared.activeFem") : t("shared.inactiveFem")}
                                            color={s.status ? "success" : "default"}
                                            variant="outlined"
                                          />
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Tooltip title={t("shared.edit")}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              setSubDialog({ open: true, parentId: cat.id, sub: s })
                                            }
                                          >
                                            <EditRoundedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("shared.delete")}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              onDeleteSubcategory?.({ parentId: cat.id, subId: s.id })
                                            }
                                          >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
        {/* seletor de page size */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">{t("shared.rowsPerPage")}</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>{sz}</MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage={t("shared.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("shared.of")} ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>

      {/* Dialog Subcategoria */}
      <SubDialog
        open={subDialog.open}
        initial={subDialog.sub}
        onClose={() => setSubDialog({ open: false, parentId: null, sub: null })}
        onSave={(sub) => {
          onUpsertSubcategory?.({ parentId: subDialog.parentId, sub });
          setSubDialog({ open: false, parentId: null, sub: null });
        }}
      />
    </Paper>
  );
};

export default CategoryTable;
